/* Range dimensions — size selection and the unit toggle.
 *
 * Both unit values are already in the markup as data attributes, computed in
 * Liquid, so this only ever swaps which one is on screen. Nothing is fetched
 * and no arithmetic happens here: the figures are right before this runs, and
 * stay right if it never does.
 */
(() => {
  const ACTIVE = 'is-active';

  /**
   * Shows one size's measurements and marks its pill.
   * @param {HTMLElement} root - The .range-dims element.
   * @param {string} index - The size's index, as a string.
   */
  const showSize = (root, index) => {
    root.querySelectorAll('[data-size-index]').forEach((pill) => {
      const on = pill.dataset.sizeIndex === index;
      pill.classList.toggle(ACTIVE, on);
      pill.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    root.querySelectorAll('[data-size-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.sizePanel !== index;
    });
  };

  /**
   * Rewrites every figure to the requested unit.
   * @param {HTMLElement} root - The .range-dims element.
   * @param {string} unit - 'in' or 'cm'.
   */
  const showUnit = (root, unit) => {
    if (unit !== 'in' && unit !== 'cm') return;

    root.querySelectorAll('[data-unit]').forEach((button) => {
      const on = button.dataset.unit === unit;
      button.classList.toggle(ACTIVE, on);
      button.setAttribute('aria-pressed', on ? 'true' : 'false');
    });

    root.querySelectorAll('.range-dims__figure').forEach((figure) => {
      const value = figure.dataset[unit];
      // Cells with nothing to convert carry no data attributes, so they are
      // left exactly as authored.
      if (value !== undefined) figure.textContent = value;
    });

    root.querySelectorAll('.range-dims__unit-suffix').forEach((suffix) => {
      suffix.textContent = unit;
    });
  };

  const init = (root) => {
    root.addEventListener('click', (event) => {
      const size = event.target.closest('[data-size-index]');
      if (size) {
        showSize(root, size.dataset.sizeIndex);
        return;
      }

      const unit = event.target.closest('[data-unit]');
      if (unit) showUnit(root, unit.dataset.unit);
    });

    // Left and right walk the sizes, as a tablist is expected to.
    root.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

      const current = event.target.closest('[data-size-index]');
      if (!current) return;

      const pills = [...root.querySelectorAll('[data-size-index]')];
      const step = event.key === 'ArrowRight' ? 1 : -1;
      const next = pills[pills.indexOf(current) + step];
      if (!next) return;

      event.preventDefault();
      showSize(root, next.dataset.sizeIndex);
      next.focus();
    });
  };

  const start = () => document.querySelectorAll('[data-range-dims]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', start);
})();
